import {
  createFeelingTagApi,
  createGenresTagApi,
  createTagApi,
  deleteGenresTagApi,
  fetchFeelingTags,
  fetchGenresTags,
  fetchTagsApi,
  updateFeelingsTagApi,
  updateGenresTagApi,
} from "@src/apis/tagsApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "./queryKeys";
import { AxiosResponse } from "axios";
import { ITag } from "@src/types/tags";
import { ApiResponse } from "@src/types/httpApi";
import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchMoods } from "@src/apis/moodsApis";
import { IMood } from "@src/types/mood";

export const useFetchTagsInfiniteQuery = ({
  searchText,
  pageSize = 20,
  enabled = true,
}: {
  searchText?: string;
  pageSize?: number;
  enabled?: boolean;
}) =>
  useInfiniteQuery<AxiosResponse<ApiResponse<ITag[]>>, Error>({
    queryKey: [QUERY_KEYS.GET_TAGS, searchText],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        return await fetchTagsApi({
          searchText,
          page: pageParam as number,
          pageSize,
        });
      } catch (error) {
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<ITag[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    placeholderData: (previousData) => previousData,
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: false,
  });

export const useFetchMoodsInfiniteQuery = ({
  searchText,
  pageSize = 20,
}: {
  searchText?: string;
  pageSize?: number;
}) =>
  useInfiniteQuery<AxiosResponse<ApiResponse<IMood[]>>, Error>({
    queryKey: [QUERY_KEYS.GET_MOODS, searchText],
    queryFn: async ({ pageParam = 1 }) =>
      fetchMoods({
        searchText: searchText ?? "",
        page: pageParam as number,
        pageSize,
        title: searchText ?? "",
        description: searchText ?? "",
        color: searchText ?? "",
        platform: "",
        sorting: {
          field: "",
          direction: "asc",
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<IMood[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

// mutation to create tag
export const useCreateTagMutation = () => {
  return useMutation({
    mutationKey: [QUERY_KEYS.CREATE_TAG],
    mutationFn: ({ title }: { title: string }) => createTagApi({ title }),
  });
};

export const useFetchGenresTagsQuery = ({
  searchText,
}: {
  searchText?: string;
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_GENRES_TAGS, searchText],
    queryFn: () => fetchGenresTags({ searchText }),
  });
};

export const useFetchGenresTagsInfiniteQuery = ({
  searchText,
  pageSize = 20,
  enabled = true,
}: {
  searchText?: string;
  pageSize?: number;
  enabled?: boolean;
}) =>
  useInfiniteQuery<AxiosResponse<ApiResponse<ITag[]>>, Error>({
    queryKey: [QUERY_KEYS.GET_GENRES_TAGS, searchText],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        return await fetchGenresTags({
          searchText,
          page: pageParam as number,
          pageSize,
        });
      } catch (error) {
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<ITag[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    placeholderData: (previousData) => previousData,
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: false,
  });

export const useCreateGenreTag = () => {
  return useMutation({
    mutationKey: [QUERY_KEYS.CREATE_GENRE_TAG],
    mutationFn: ({ title }: { title: string }) => createGenresTagApi({ title }),
  });
};
export const useUpdateGenreTag = () => {
  return useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_GENRE_TAG],
    mutationFn: ({ title, tagId }: { title: string; tagId: string }) =>
      updateGenresTagApi({ tagId, title }),
  });
};
export const useDeleteGenreTag = () => {
  return useMutation({
    mutationKey: [QUERY_KEYS.DELETE_GENRE_TAG],
    mutationFn: ({ tagId }: { tagId: string }) => deleteGenresTagApi({ tagId }),
  });
};

export const useFetchFeelingTagsQuery = ({
  searchText,
}: {
  searchText?: string;
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_FEELING_TAGS, searchText],
    queryFn: () => fetchFeelingTags({ searchText }),
  });
};

export const useFetchFeelingTagsInfiniteQuery = ({
  searchText,
  pageSize = 20,
  enabled = true,
}: {
  searchText?: string;
  pageSize?: number;
  enabled?: boolean;
}) =>
  useInfiniteQuery<AxiosResponse<ApiResponse<ITag[]>>, Error>({
    queryKey: [QUERY_KEYS.GET_FEELING_TAGS, searchText],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        return await fetchFeelingTags({
          searchText,
          page: pageParam as number,
          pageSize,
        });
      } catch (error) {
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<ITag[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    placeholderData: (previousData) => previousData,
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: false,
  });

export const useCreateFeelingTag = () => {
  return useMutation({
    mutationKey: [QUERY_KEYS.CREATE_FEELING_TAG],
    mutationFn: ({ title }: { title: string }) =>
      createFeelingTagApi({ title }),
  });
};

export const useUpdateFeelingTag = () => {
  return useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_FEELING_TAG],
    mutationFn: ({ title, tagId }: { title: string; tagId: string }) =>
      updateFeelingsTagApi({ tagId, title }),
  });
};

export const useDeleteFeelingTag = () => {
  return useMutation({
    mutationKey: [QUERY_KEYS.DELETE_FEELING_TAG],
    mutationFn: ({ tagId }: { tagId: string }) => deleteGenresTagApi({ tagId }),
  });
};
